(function () {
  'use strict';

  const STORAGE_PREFIX = 'aluma:vimeo:t:';
  const THROTTLE_MS = 5000;
  const SDK_URL = 'https://player.vimeo.com/api/player.js';

  function extractVideoId(iframe) {
    const src = iframe.getAttribute('src') || '';
    const match = src.match(/vimeo\.com\/video\/(\d+)/);
    return match ? match[1] : null;
  }

  function loadSDK() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
    if (window.__vimeoSdkPromise) return window.__vimeoSdkPromise;
    window.__vimeoSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="' + SDK_URL + '"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }
      const s = document.createElement('script');
      s.src = SDK_URL;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return window.__vimeoSdkPromise;
  }

  function extractLessonId() {
    // Tevello lesson URLs contain a UUID segment, e.g. /lessons/b8bcf5f6-a932-45a1-96f5-a2c295adb861
    const match = window.location.pathname.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    return match ? match[1] : null;
  }

  function attachResume(iframe) {
    if (iframe.dataset.vimeoResumeAttached === '1') return;
    const videoId = extractVideoId(iframe);
    if (!videoId) return;
    iframe.dataset.vimeoResumeAttached = '1';

    const storageKey = STORAGE_PREFIX + videoId;
    const player = new window.Vimeo.Player(iframe);
    let lastWrite = 0;

    player.ready().then(() => {
      let stored;
      try { stored = localStorage.getItem(storageKey); } catch (e) { return; }
      const seconds = parseFloat(stored);
      if (!isFinite(seconds) || seconds <= 0) return;
      player.getDuration().then((duration) => {
        if (!isFinite(duration) || seconds >= duration - 2) return;
        player.setCurrentTime(seconds).catch(() => {});
      }).catch(() => {});
    }).catch(() => {});

    player.on('timeupdate', (data) => {
      const now = Date.now();
      if (now - lastWrite < THROTTLE_MS) return;
      lastWrite = now;
      try { localStorage.setItem(storageKey, String(data.seconds)); } catch (e) {}
    });

    player.on('ended', () => {
      try { localStorage.removeItem(storageKey); } catch (e) {}
    });

    // 70% genuine-watch tracker — isolated so a bug here cannot break resume above
    try {
      const W70_PREFIX = 'aluma:vimeo:w70:';
      const w70Key = W70_PREFIX + videoId;
      let lastSeen = null;
      let hasFired = false;

      // Honour a prior fire in the same browser (e.g. page reload)
      try { if (localStorage.getItem(w70Key) === '1') hasFired = true; } catch (e) {}

      // F3: fetch the duration once from the player rather than trusting the
      // per-tick payload, which can be missing/zero and would silently block
      // every fire. Mirrors the resume block above. Falls back to data.duration
      // until this resolves.
      let knownDuration = 0;
      player.getDuration().then(function (d) {
        if (isFinite(d) && d > 0) knownDuration = d;
      }).catch(function () {});

      // Count genuine watch time from zero each attach. We deliberately do NOT
      // seed from the saved resume position — that's a playhead position, not
      // watched time, and seeding it lets a forward-scrub false-fire (see F1).
      let watchedSeconds = 0;

      player.on('timeupdate', function (data) {
        if (hasFired) return;
        const current = data.seconds;
        const duration = knownDuration || data.duration;
        if (!isFinite(duration) || duration <= 0) return;

        if (lastSeen !== null) {
          const delta = current - lastSeen;
          // Only count small forward steps — seeks (large jumps or backward) are excluded
          if (delta > 0 && delta < 2) {
            watchedSeconds += delta;
          }
        }
        lastSeen = current;

        if (watchedSeconds / duration >= 0.70) {
          // Klaviyo onsite can be momentarily unready on these late-loading
          // lesson pages. If it's not ready, do NOT set the guards — just skip
          // this tick and let the next one retry. (F2)
          if (!window.klaviyo || typeof window.klaviyo.track !== 'function') return;
          var props = {
            VideoId: videoId,
            PercentThreshold: 70,
            WatchedSeconds: Math.round(watchedSeconds),
            Duration: Math.round(duration),
            Source: 'vimeo-client'
          };
          var lessonId = extractLessonId();
          if (lessonId) props.LessonId = lessonId;
          try {
            window.klaviyo.track('Watched Video 70%', props);
            // Only mark it done once the event has actually gone out.
            hasFired = true;
            try { localStorage.setItem(w70Key, '1'); } catch (e) {}
          } catch (ke) {
            // track threw — leave the guards unset so a later tick retries.
          }
        }
      });
    } catch (e) {}
  }

  function processIframe(iframe) {
    if (!iframe || iframe.tagName !== 'IFRAME') return;
    if (!/player\.vimeo\.com\/video\//.test(iframe.src || '')) return;
    loadSDK().then(() => attachResume(iframe)).catch(() => {});
  }

  function scanAll(root) {
    const iframes = (root || document).querySelectorAll('iframe[src*="player.vimeo.com/video/"]');
    iframes.forEach(processIframe);
  }

  function startObserver() {
    const target = document.body || document.documentElement;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IFRAME') {
            processIframe(node);
          } else if (node.querySelectorAll) {
            scanAll(node);
          }
        });
        if (m.type === 'attributes' && m.target.tagName === 'IFRAME') {
          processIframe(m.target);
        }
      }
    });
    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });
  }

  function init() {
    scanAll(document);
    startObserver();
    // Polling fallback. Tevello injects the lesson's Vimeo iframe via client-side
    // navigation that the MutationObserver above does not reliably catch (proven
    // live 2026-06-12: iframe present, SDK loaded, klaviyo ready, but the player
    // never auto-attached). A 1s document scan is cheap (one querySelector) and
    // the per-iframe dataset guard in attachResume prevents double-attach. Runs
    // indefinitely so navigating lesson-to-lesson re-attaches to each new video.
    setInterval(function () { scanAll(document); }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => scanAll(document));
})();
